import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item, isInsuranceItem,
  findPostPurchaseCheckout,
  isBedbankItem,
} from 'lib/checkout/checkoutUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import config from 'constants/config'
import { PAYMENT_SCHEDULE_ENABLED_REGIONS } from 'constants/config/region'
import { sum } from 'lib/array/arrayUtils'
import { getAllOffers } from 'selectors/offerSelectors'

export const isPaymentScheduleEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (_state: App.State) => config.PAYMENT_SCHEDULE_ENABLED,
  (currentRegionCode, isEnabled) => {
    if (!isEnabled) {
      return false
    }

    if (!PAYMENT_SCHEDULE_ENABLED_REGIONS.includes(currentRegionCode)) {
      return false
    }

    return true
  },
)

const isItemAllowedWithPaymentSchedules = (item: App.Checkout.AnyItem): boolean => {
  return isTourV2Item(item) ||
  isTourV2ExperienceItem(item) ||
  isLuxPlusSubscriptionItem(item) ||
  isSubscriptionJoinItem(item) ||
  isInsuranceItem(item) ||
  (isBedbankItem(item))
}

const canGetItemsPaymentScheduleInfo = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.offer.bedbankOfferRates,
  (items, offers, isGift, bedbankRatesByOffer): Array<boolean> | false => {
    if (isGift || (!items.every(isItemAllowedWithPaymentSchedules))) {
      return false
    }

    const tourV2Items = items.filter(isTourV2Item)
    const bedbankItems = items.filter(isBedbankItem)

    // Payment schedules for bedbank items are only supported when there is exactly one bedbank item and no tour items
    const hasOnlyOneBedbankItem = bedbankItems.length === 1
    const hasNoTourItems = tourV2Items.length === 0
    if (hasNoTourItems && !hasOnlyOneBedbankItem) {
      return false
    }

    return [...tourV2Items, ...bedbankItems].map((item) => {
      if (isTourV2Item(item) || (isBedbankItem(item) && tourV2Items.length > 0)) {
        const tourItem = isTourV2Item(item) ? item : tourV2Items[0]
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(tourItem)] as App.Tours.TourV2Offer | undefined
        const templateId = tourV2Offer?.paymentScheduleInfo?.templateId
        return !!templateId
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankOfferRates = bedbankRatesByOffer[item.offerId]?.[rateKey]
        const bedbankRate = bedbankOfferRates?.find(rate => rate.id === item.roomRateId)
        return !!bedbankRate?.paymentScheduleInfo?.templateId
      }
      return false
    })
  },
)

const isAnyItemDisabledForPaymentSchedule = createSelector(
  canGetItemsPaymentScheduleInfo,
  (itemsPaymentScheduleInfo): boolean => !itemsPaymentScheduleInfo || itemsPaymentScheduleInfo.some(item => !item),
)

export const isPaymentScheduleAvailable = createSelector(
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isAnyItemDisabledForPaymentSchedule(state),
  (
    isPaymentScheduleEnabled,
    postPurchase,
    isAnyItemDisabledForPaymentSchedule,
  ): boolean => {
    const result = (
      isPaymentScheduleEnabled &&
      !postPurchase &&
      !isAnyItemDisabledForPaymentSchedule
    )
    return result
  },
)

export const getPaymentScheduleTemplateIdOfCartItem = createSelector(
  (state: App.State) => state.offer.tourV2Offers,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.checkout.cart.items,
  (tourV2Offers, bedbankOfferRates, items): string | undefined | null => {
    const tourV2Items = items.filter(isTourV2Item)
    const bedbankItems = items.filter(isBedbankItem)

    // First check tour items
    for (const item of tourV2Items) {
      const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
      if (offer?.paymentScheduleInfo?.templateId) {
        return offer.paymentScheduleInfo.templateId
      }
    }

    // If no tour item with template ID found, check bedbank items
    for (const item of bedbankItems) {
      const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
      const bedbankOfferRatesForItem = bedbankOfferRates[item.offerId]?.[rateKey]
      const bedbankRate = bedbankOfferRatesForItem?.find(rate => rate.id === item.roomRateId)
      if (bedbankRate?.paymentScheduleInfo?.templateId) {
        return bedbankRate.paymentScheduleInfo.templateId
      }
    }

    return undefined
  },
)

export const getServiceFeeForInitialPayment = createSelector(
  (state: App.State) => state.checkout.paymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule.eligible || !paymentSchedule.payment_schedules) {
      return 0
    }
    return paymentSchedule.payment_schedules[0].feeAmount || 0
  },
)

export const getPaymentScheduleTotalServiceFee = createSelector(
  (state: App.State) => state.checkout.paymentSchedule,
  (paymentSchedule): number => {
    if (!paymentSchedule.eligible || !paymentSchedule.payment_schedules) {
      return 0
    }
    return sum(paymentSchedule.payment_schedules, (schedule) => schedule.feeAmount)
  },
)

export const getServiceFeeForLaterPayments = createSelector(
  getPaymentScheduleTotalServiceFee,
  getServiceFeeForInitialPayment,
  (totalServiceFee, initialServiceFee): number => {
    return totalServiceFee - initialServiceFee
  },
)

export const getServiceFeePaymentScheduleData = createSelector(
  (state: App.State) => state.checkout.paymentSchedule,
  (paymentSchedule): App.PaymentScheduleServiceFeeData | undefined => {
    const chargeIndex = paymentSchedule.payment_schedules.findIndex(schedule => schedule.feeAmount > 0)

    if (chargeIndex === -1) {
      return undefined
    }

    return {
      chargeIndex,
      feeAmount: paymentSchedule.payment_schedules[chargeIndex].feeAmount,
      paymentType: paymentSchedule.payment_schedules[chargeIndex].paymentType,
    }
  },
)
